/**
 * Service worker registration.
 *
 * Registers only in production and only after `load`, so the worker never
 * competes with the first paint. On localhost it additionally verifies that a
 * service worker is really being served before registering, which avoids the
 * classic "stale worker from another project on :3000" problem.
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

export function register(config?: Config): void {
  if (!('serviceWorker' in navigator)) return;

  // A worker registered by an earlier production build (`serve -s build` on :3000)
  // keeps controlling that origin in dev and serves its precached bundle to the dev
  // server. The stale chunks then meet a fresh webpack runtime, which surfaces as
  // "originalFactory.call is not a function" from react-refresh. Clear it out.
  // `ready` never settles when nothing is registered, so enumerate instead.
  if (process.env.NODE_ENV !== 'production') {
    navigator.serviceWorker
      .getRegistrations()
      .then(registrations => registrations.forEach(registration => registration.unregister()))
      .catch(() => {});
    return;
  }

  // A worker cannot control pages on a different origin
  const publicUrl = new URL(process.env.PUBLIC_URL as string, window.location.href);
  if (publicUrl.origin !== window.location.origin) return;

  window.addEventListener('load', () => {
    const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

    if (isLocalhost) {
      checkValidServiceWorker(swUrl, config);
    } else {
      registerValidSW(swUrl, config);
    }
  });
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.onstatechange = () => {
          if (installingWorker.state !== 'installed') return;

          if (navigator.serviceWorker.controller) {
            // Updated content is cached but the old worker still controls the
            // page; it takes effect after all tabs are closed.
            config?.onUpdate?.(registration);
          } else {
            config?.onSuccess?.(registration);
          }
        };
      };
    })
    .catch(error => {
      console.error('Service worker registration failed:', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then(response => {
      const contentType = response.headers.get('content-type');
      const missing = response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1);

      if (missing) {
        // No service worker here — drop any stale one and reload
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => window.location.reload());
        });
      } else {
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      console.log('No internet connection found. App is running in offline mode.');
    });
}

export function unregister(): void {
  if (!('serviceWorker' in navigator)) return;
  navigator.serviceWorker.ready
    .then(registration => registration.unregister())
    .catch(error => console.error(error));
}
